import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { ReviewSummaryCard } from "@/components/ui/card-2";
import { motion, useScroll, useTransform } from "framer-motion";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";

const Hero = () => {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section
      ref={ref}
      className="relative flex flex-col md:block lg:flex lg:items-center min-h-screen overflow-hidden bg-[#192847] pt-[80px] md:pt-[100px] lg:pt-24"
    >
      {/* Mobile & Desktop Background Image */}
      <div className="absolute inset-0 z-0 block md:hidden lg:block overflow-hidden">
        <motion.img
          style={{ y, scale: 1.1 }}
          src="https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a30a307d64da5ce6b7c3c23.webp"
          alt="Advogados Pinheiro"
          className="w-full h-full object-cover object-[center_top] lg:object-center origin-top"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-[#192847]/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#192847]/10 via-[#192847]/20 to-[#192847]/60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(25,40,71,0.6)_0%,_transparent_50%)]" />
      </div>

      {/* Tablet Top Image */}
      <div className="w-full hidden md:block lg:hidden overflow-hidden relative">
        <motion.img
          style={{ y, scale: 1.1 }}
          src="https://assets.cdn.filesafe.space/vyxGSs9NtFYsqksgNt9c/media/6a30a307d64da5ce6b7c3c23.webp"
          alt="Advogados Pinheiro"
          className="w-full h-auto object-cover object-[center_top] origin-top"
          fetchPriority="high"
        />
      </div>

      {/* Content Section */}
      <div className="container relative z-10 mx-auto px-4 py-12 md:py-16 lg:py-20 flex flex-col items-center justify-center flex-grow lg:min-h-[calc(100vh-6rem)] pt-[40px] md:pt-0 pb-[98px]">
        <div className="max-w-4xl text-white flex flex-col items-center text-center lg:mt-24">
          <h1 className="text-[30px] md:text-[48px] lg:text-[60px] font-bold font-serif leading-[1.1] mb-8 [text-shadow:_0_4px_30px_rgba(0,0,0,0.9),_0_2px_10px_rgba(0,0,0,0.8)] md:[text-shadow:none] lg:[text-shadow:_0_4px_30px_rgba(0,0,0,0.9),_0_2px_10px_rgba(0,0,0,0.8)] pl-[10px] pr-[10px] mt-5">
            Sua trajetória no serviço público merece o respaldo de
            especialistas.
          </h1>

          <p className="text-lg md:text-xl lg:text-2xl text-white mb-10 max-w-4xl leading-relaxed [text-shadow:_0_4px_20px_rgba(0,0,0,0.9),_0_2px_10px_rgba(0,0,0,0.8)] md:[text-shadow:none] lg:[text-shadow:_0_4px_20px_rgba(0,0,0,0.9),_0_2px_10px_rgba(0,0,0,0.8)] font-medium px-0 md:px-[50px]">
            <span className="inline-block bg-accent text-accent-foreground px-3 py-1 rounded-sm [text-shadow:none] font-semibold mb-2 whitespace-normal md:whitespace-nowrap pl-[9px] pr-[9px] mr-0 ml-0">
              Escritório de advocacia especializado em Servidores Públicos.
            </span>
            <br />
            Atuamos na correção de erros de cálculo, gratificações retidas, teto
            salarial e em problemas com a aposentadoria (SPPREV).
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-6 w-full sm:w-auto mb-[28px] lg:mb-[98px]">
            <button
              onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent("open-home-popup"));
              }}
              className="inline-flex items-center justify-center gap-2 sm:gap-3 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-[85%] sm:w-auto font-bold px-6 py-4 sm:px-8 sm:py-[18px] h-auto rounded-full bg-[#075E54] hover:bg-[#043b35] text-white border-2 border-white shadow-lg hover:scale-105 transition-transform uppercase text-sm sm:text-base mx-auto sm:mx-0"
              aria-label="Entrar em contato"
            >
              <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              Entrar em contato
            </button>
          </div>

          <div className="flex flex-col gap-4 mt-0 lg:mt-0">
            <ReviewSummaryCard
              rating={5.0}
              reviewCount={3000}
              summaryText=""
              className="bg-[#2a364f]/70 border-white/20 shadow-2xl text-white backdrop-blur-md mx-auto"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
